import { useCallback, useEffect, useRef, useState } from "react";
import { T, base } from "../lib/theme";
import { API, apiFetch } from "../lib/api";

// ── Helpers ───────────────────────────────────────────────────────
const statusCfg = {
  draft:     { color: "#64748b", dim: "rgba(100,116,139,0.12)", label: "Draft"      },
  active:    { color: "#f59e0b", dim: "rgba(245,158,11,0.12)",  label: "Sending…"   },
  paused:    { color: "#818cf8", dim: "rgba(129,140,248,0.12)", label: "Paused"     },
  completed: { color: "#10b981", dim: "rgba(16,185,129,0.12)",  label: "Completed"  },
};

const leadStatusCfg = {
  pending:   { color: "#64748b", label: "Pending"  },
  sent:      { color: "#10b981", label: "Sent"     },
  failed:    { color: "#f43f5e", label: "Failed"   },
  opted_out: { color: "#f97316", label: "Opted out"},
};

function Pill({ status, cfg }) {
  const c = cfg[status] || { color: "#64748b", label: status };
  const spin = status === "active";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.dim || `${c.color}18`, color: c.color, border: `1px solid ${c.color}33` }}>
      {spin && <div style={{ width: 7, height: 7, border: `1.5px solid ${c.color}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />}
      {c.label}
    </span>
  );
}

function ProgressBar({ sent, total, failed }) {
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.text3, marginBottom: 4 }}>
        <span>{sent} / {total} sent</span>
        <span>{pct}%{failed > 0 ? ` · ${failed} failed` : ""}</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: T.bg2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#6366f1,#10b981)", borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
}

// ── Create / Edit Campaign Modal ──────────────────────────────────
function CampaignModal({ initial, pipelines, onSave, onClose }) {
  const [name,     setName]     = useState(initial?.name     || "");
  const [msg,      setMsg]      = useState(initial?.templateMessage || "Hi {name}, thanks for reaching out! How can we help you today? 😊");
  const [source,   setSource]   = useState(initial?.sourceType || "manual");
  const [pipeline, setPipeline] = useState(initial?.pipelineId || "");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const save = async () => {
    if (!name.trim()) { setError("Campaign name is required"); return; }
    if (!msg.trim())  { setError("Template message is required"); return; }
    setLoading(true); setError("");
    try {
      await onSave({ name: name.trim(), templateMessage: msg.trim(), sourceType: source, pipelineId: pipeline || null });
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = (value, onChange, placeholder, multiline) => {
    const style = { width: "100%", boxSizing: "border-box", padding: multiline ? "10px 13px" : "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 13, fontFamily: base.font, outline: "none", resize: multiline ? "vertical" : "none" };
    if (multiline) return <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} style={style} onFocus={e => (e.target.style.borderColor = "#6366f1")} onBlur={e => (e.target.style.borderColor = T.border2)} />;
    return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={style} onFocus={e => (e.target.style.borderColor = "#6366f1")} onBlur={e => (e.target.style.borderColor = T.border2)} />;
  };

  const lbl = (text) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>{text}</label>;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "0 16px" }}>
      <div style={{ background: T.bg1, borderRadius: 20, padding: 28, width: "100%", maxWidth: 480, border: `1px solid ${T.border2}`, boxShadow: "0 24px 60px rgba(0,0,0,0.45)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text1, marginBottom: 22 }}>{initial ? "Edit Campaign" : "New Campaign"}</div>

        {error && <div style={{ padding: "9px 13px", borderRadius: 9, background: "rgba(244,63,94,0.1)", color: "#f43f5e", border: "1px solid rgba(244,63,94,0.2)", fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <div style={{ marginBottom: 16 }}>{lbl("Campaign Name")}{inp(name, setName, "e.g. January Leads, New Customers")}</div>

        <div style={{ marginBottom: 16 }}>
          {lbl("How will you add contacts?")}
          <div style={{ display: "flex", gap: 8 }}>
            {[
              ["manual",        "📋 Upload / Paste", "CSV, Excel, or type numbers"],
              ["google_sheets", "📊 Google Sheet",   "Sync from a shared sheet"],
            ].map(([val, title, desc]) => (
              <button key={val} onClick={() => setSource(val)} style={{ flex: 1, padding: "11px 12px", borderRadius: 10, border: `1.5px solid ${source === val ? "#6366f1" : T.border2}`, background: source === val ? "rgba(99,102,241,0.1)" : "none", cursor: "pointer", textAlign: "left", fontFamily: base.font }}>
                <div style={{ fontSize: 12.5, fontWeight: source === val ? 700 : 600, color: source === val ? "#818cf8" : T.text1 }}>{title}</div>
                <div style={{ fontSize: 10.5, color: T.text3, marginTop: 3, fontWeight: 400, lineHeight: 1.3 }}>{desc}</div>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: T.text3, marginTop: 7 }}>You can add or import contacts after creating the campaign.</div>
        </div>

        <div style={{ marginBottom: 16 }}>
          {lbl("Template Message")}
          {inp(msg, setMsg, "Hi {name}, …", true)}
          <div style={{ fontSize: 11, color: T.text3, marginTop: 5 }}>Use <code style={{ background: T.bg2, padding: "1px 5px", borderRadius: 4 }}>{"{name}"}</code> and <code style={{ background: T.bg2, padding: "1px 5px", borderRadius: 4 }}>{"{phone}"}</code> as placeholders</div>
        </div>

        {pipelines.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {lbl("Add to Pipeline (optional)")}
            <select value={pipeline} onChange={e => setPipeline(e.target.value)}
              style={{ width: "100%", padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 13, fontFamily: base.font, outline: "none" }}>
              <option value="">None — don't add to pipeline</option>
              {pipelines.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 11, borderRadius: 10, border: `1px solid ${T.border2}`, background: "none", color: T.text2, cursor: "pointer", fontSize: 13, fontFamily: base.font }}>Cancel</button>
          <button onClick={save} disabled={loading} style={{ flex: 2, padding: 11, borderRadius: 10, border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font }}>
            {loading ? "Saving…" : (initial ? "Save Changes" : "Create Campaign")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Campaign Detail Panel ─────────────────────────────────────────
function CampaignDetail({ campaign, token, socket, onUpdate, onBack, showToast }) {
  const [leads,     setLeads]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [filter,    setFilter]    = useState("");
  const [loading,   setLoading]   = useState(false);
  const [phones,    setPhones]    = useState("");    // manual entry textarea
  const [sheetsUrl, setSheetsUrl] = useState(campaign.sheetsUrl || "");
  const [syncing,   setSyncing]   = useState(false);
  const [tabs,      setTabs]      = useState([]);      // [{name, gid}] of the pasted sheet
  const [selectedGid, setSelectedGid] = useState(() => (campaign.sheetsUrl || "").match(/[#&?]gid=(\d+)/)?.[1] || "");
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendMode,  setSendMode]  = useState(campaign.sendMode || "template");
  const [tplMsg,    setTplMsg]    = useState(campaign.templateMessage || "");
  const [aiInstr,   setAiInstr]   = useState(campaign.aiInstruction || "");
  const [savingMsg, setSavingMsg] = useState(false);
  const [pipelines,  setPipelines]  = useState([]);
  const [pipelineId, setPipelineId] = useState(campaign.pipelineId || "");
  const [savingPipe, setSavingPipe] = useState(false);
  const fileRef = useRef(null);

  // Load the workspace's pipelines so the campaign can be linked to one.
  useEffect(() => {
    apiFetch("/api/pipelines", {}, token).then(d => setPipelines(d.pipelines || [])).catch(() => {});
  }, [token]);

  // Link/change the pipeline. New synced leads land in it automatically; for a
  // Google Sheet we re-sync right away so existing leads get backfilled too.
  const savePipeline = async (pid) => {
    setPipelineId(pid);
    setSavingPipe(true);
    try {
      await apiFetch(`/api/outbound/campaigns/${campaign._id}`, { method: "PUT", body: JSON.stringify({ pipelineId: pid || null }) }, token);
      onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, pipelineId: pid || null } : c));
      showToast(pid ? "Pipeline linked — new leads will be added automatically" : "Pipeline unlinked");
      if (pid && campaign.sourceType === "google_sheets" && sheetsUrl.trim()) {
        await apiFetch(`/api/outbound/campaigns/${campaign._id}/sync-sheets`, { method: "POST", silent: true }, token).catch(() => {});
        loadLeads(1, filter);
      }
    } catch { showToast("Failed to link pipeline", "error"); }
    setSavingPipe(false);
  };

  const loadLeads = useCallback(async (pg = 1, st = "") => {
    try {
      const q = `?page=${pg}&limit=50${st ? "&status=" + st : ""}`;
      const d = await apiFetch(`/api/outbound/campaigns/${campaign._id}/leads${q}`, {}, token);
      setLeads(d.leads || []);
      setTotal(d.total || 0);
    } catch {}
  }, [campaign._id, token]);

  useEffect(() => { loadLeads(1, filter); }, [campaign._id, filter]);

  // Live progress via socket
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (data.campaignId !== campaign._id) return;
      onUpdate(prev => prev.map(c => c._id === campaign._id
        ? { ...c, status: data.status || c.status, sentCount: data.sentCount ?? c.sentCount }
        : c));
      if (data.leadId) {
        setLeads(prev => prev.map(l => l._id === data.leadId ? { ...l, status: data.status === "sent" ? "sent" : l.status } : l));
      }
      if (data.autoSynced || data.autoRemoved) {
        onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, totalLeads: Math.max(0, (c.totalLeads || 0) + (data.autoSynced || 0) - (data.autoRemoved || 0)) } : c));
        loadLeads(1, filter);
        if (data.autoSynced) showToast(`${data.autoSynced} new lead(s) auto-synced from sheet`);
      }
    };
    socket.on("outbound_progress", handler);
    return () => socket.off("outbound_progress", handler);
  }, [socket, campaign._id, onUpdate, loadLeads, filter, showToast]);

  const addManual = async () => {
    const lines = phones.split("\n").map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;
    const leads = lines.map(l => {
      const parts = l.split(/[\t,]/).map(p => p.trim());
      return parts.length >= 2 ? { phone: parts[0], name: parts[1] } : { phone: parts[0], name: "" };
    });
    try {
      const d = await apiFetch(`/api/outbound/campaigns/${campaign._id}/add-leads`, { method: "POST", body: JSON.stringify({ leads }) }, token);
      if (d.error) { showToast(d.error, "error"); return; }
      showToast(`${d.added} lead(s) added`);
      setPhones("");
      onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, totalLeads: (c.totalLeads || 0) + d.added } : c));
      loadLeads(1, filter);
    } catch { showToast("Failed to add leads", "error"); }
  };

  const uploadCSV = async (file) => {
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await fetch(`${API}/api/outbound/campaigns/${campaign._id}/upload-csv`, {
        method: "POST", headers: { authorization: token }, body: form,
      });
      const d = await r.json();
      if (d.error) { showToast(d.error, "error"); } else {
        showToast(`${d.added} lead(s) imported from file`);
        onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, totalLeads: (c.totalLeads || 0) + d.added } : c));
        loadLeads(1, filter);
      }
    } catch { showToast("Upload failed", "error"); }
    setUploading(false);
  };

  // Build the final URL the backend syncs from — always carries the chosen tab's gid.
  const urlWithGid = (url, gid) => {
    const base = (url || "").trim().replace(/([#&?])gid=\d+/g, "").replace(/[#&?]+$/, "");
    return gid ? `${base}#gid=${gid}` : base;
  };

  // Fetch the sheet's tabs so the user can pick the right page.
  const loadTabs = useCallback(async (url) => {
    const u = (url ?? sheetsUrl).trim();
    if (!/\/spreadsheets\/d\//.test(u)) { setTabs([]); return; }
    setLoadingTabs(true);
    try {
      const d = await apiFetch(`/api/outbound/sheet-tabs?url=${encodeURIComponent(u)}`, { silent: true }, token);
      if (Array.isArray(d.tabs) && d.tabs.length) {
        setTabs(d.tabs);
        // Default to the current gid, else the first tab.
        setSelectedGid(prev => (prev && d.tabs.some(t => t.gid === prev)) ? prev : d.tabs[0].gid);
      } else {
        setTabs([]);
        if (d.error) showToast(d.error, "warn");
      }
    } catch { setTabs([]); }
    setLoadingTabs(false);
  }, [sheetsUrl, token, showToast]);

  // Auto-load tabs on mount if a URL is already saved.
  useEffect(() => { if (sheetsUrl.trim()) loadTabs(sheetsUrl); /* eslint-disable-next-line */ }, []);

  const syncSheets = async () => {
    if (!sheetsUrl.trim()) { showToast("Enter a Google Sheets URL first", "warn"); return; }
    if (tabs.length > 1 && !selectedGid) { showToast("Select which sheet tab to sync", "warn"); return; }
    setSyncing(true);
    try {
      const finalUrl = urlWithGid(sheetsUrl, selectedGid);
      // Save URL (with the chosen tab) first
      await apiFetch(`/api/outbound/campaigns/${campaign._id}`, { method: "PUT", body: JSON.stringify({ sheetsUrl: finalUrl }) }, token);
      const d = await apiFetch(`/api/outbound/campaigns/${campaign._id}/sync-sheets`, { method: "POST" }, token);
      if (d.error) { showToast(d.error, "error"); }
      else {
        showToast(d.removed > 0 ? `${d.added} added · ${d.removed} removed` : `${d.added} new lead(s) pulled from sheet`);
        onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, totalLeads: Math.max(0, (c.totalLeads || 0) + (d.added || 0) - (d.removed || 0)) } : c));
        loadLeads(1, filter);
      }
    } catch { showToast("Sync failed", "error"); }
    setSyncing(false);
  };

  const clearLeads = async () => {
    if (!window.confirm("Remove ALL leads from this campaign? This can't be undone. (You can re-sync your sheet or re-upload afterwards.)")) return;
    try {
      const d = await apiFetch(`/api/outbound/campaigns/${campaign._id}/leads`, { method: "DELETE" }, token);
      if (d.error) { showToast(d.error, "error"); return; }
      onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, totalLeads: 0, sentCount: 0, failedCount: 0, status: c.status === "active" ? "paused" : c.status } : c));
      setFilter(""); loadLeads(1, "");
      showToast(`Cleared ${d.deleted} lead(s)`);
    } catch { showToast("Failed to clear leads", "error"); }
  };

  const saveMessage = async () => {
    if (sendMode === "template" && !tplMsg.trim()) { showToast("Enter a message first", "warn"); return; }
    if (sendMode === "ai" && !aiInstr.trim())      { showToast("Describe what the AI should say", "warn"); return; }
    setSavingMsg(true);
    try {
      await apiFetch(`/api/outbound/campaigns/${campaign._id}`, {
        method: "PUT",
        body: JSON.stringify({ sendMode, templateMessage: tplMsg, aiInstruction: aiInstr }),
      }, token);
      onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, sendMode, templateMessage: tplMsg, aiInstruction: aiInstr } : c));
      showToast("Message saved");
    } catch { showToast("Failed to save message", "error"); }
    setSavingMsg(false);
  };

  const launch = async () => {
    try {
      const d = await apiFetch(`/api/outbound/campaigns/${campaign._id}/launch`, { method: "POST" }, token);
      if (d.error) { showToast(d.error, "error"); return; }
      showToast(`Campaign launched — sending to ${d.pending} leads`);
      onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, status: "active" } : c));
    } catch { showToast("Launch failed", "error"); }
  };

  const pause = async () => {
    try {
      await apiFetch(`/api/outbound/campaigns/${campaign._id}/pause`, { method: "POST" }, token);
      onUpdate(prev => prev.map(c => c._id === campaign._id ? { ...c, status: "paused" } : c));
      showToast("Campaign paused");
    } catch { showToast("Pause failed", "error"); }
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Detail header */}
      <div style={{ padding: "18px 28px 14px", borderBottom: `1px solid ${T.border1}`, display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: T.text3, display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontFamily: base.font, padding: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text1 }}>{campaign.name}</div>
          <div style={{ fontSize: 11, color: T.text3, marginTop: 2 }}>
            {campaign.totalLeads || 0} leads · {campaign.sentCount || 0} sent · {campaign.failedCount || 0} failed
          </div>
        </div>
        <Pill status={campaign.status} cfg={statusCfg} />
        {(campaign.status === "draft" || campaign.status === "paused") && (
          <button onClick={launch} style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font, display: "flex", alignItems: "center", gap: 7 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            Launch
          </button>
        )}
        {campaign.status === "active" && (
          <button onClick={pause} style={{ padding: "9px 18px", borderRadius: 10, border: `1px solid ${T.border2}`, background: "none", color: T.text2, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font }}>Pause</button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {/* Progress bar */}
        {(campaign.totalLeads || 0) > 0 && (
          <div style={{ background: T.bg1, border: `1px solid ${T.border2}`, borderRadius: 14, padding: "16px 20px", marginBottom: 20 }}>
            <ProgressBar sent={campaign.sentCount || 0} total={campaign.totalLeads || 0} failed={campaign.failedCount || 0} />
          </div>
        )}

        {/* Opening message / AI mode */}
        <div style={{ background: T.bg1, border: `1px solid ${T.border2}`, borderRadius: 14, padding: "18px 20px", marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, marginBottom: 3 }}>Opening Message</div>
          <div style={{ fontSize: 11, color: T.text3, marginBottom: 14 }}>The first message each contact receives. When they reply, the AI takes over the chat automatically.</div>

          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[
              ["template", "✍️ Fixed message",   "Send the exact text you write"],
              ["ai",       "🤖 AI conversation", "AI writes a personalized opener per contact"],
            ].map(([val, title, desc]) => (
              <button key={val} onClick={() => setSendMode(val)} style={{ flex: 1, padding: "11px 12px", borderRadius: 10, border: `1.5px solid ${sendMode === val ? "#6366f1" : T.border2}`, background: sendMode === val ? "rgba(99,102,241,0.1)" : "none", cursor: "pointer", textAlign: "left", fontFamily: base.font }}>
                <div style={{ fontSize: 12.5, fontWeight: sendMode === val ? 700 : 600, color: sendMode === val ? "#818cf8" : T.text1 }}>{title}</div>
                <div style={{ fontSize: 10.5, color: T.text3, marginTop: 3, fontWeight: 400, lineHeight: 1.3 }}>{desc}</div>
              </button>
            ))}
          </div>

          {sendMode === "template" ? (
            <>
              <textarea value={tplMsg} onChange={e => setTplMsg(e.target.value)} rows={3} placeholder="Hi {name}, thanks for your interest! How can we help you today?"
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 13, fontFamily: base.font, outline: "none", resize: "vertical" }}
                onFocus={e => (e.target.style.borderColor = "#6366f1")} onBlur={e => (e.target.style.borderColor = T.border2)} />
              <div style={{ fontSize: 11, color: T.text3, marginTop: 6 }}>Use <code style={{ background: T.bg2, padding: "1px 5px", borderRadius: 4 }}>{"{name}"}</code> and <code style={{ background: T.bg2, padding: "1px 5px", borderRadius: 4 }}>{"{phone}"}</code> as placeholders.</div>
            </>
          ) : (
            <>
              <textarea value={aiInstr} onChange={e => setAiInstr(e.target.value)} rows={3} placeholder="e.g. Introduce our winter sale and ask if they'd like to see the catalog"
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 13, fontFamily: base.font, outline: "none", resize: "vertical" }}
                onFocus={e => (e.target.style.borderColor = "#6366f1")} onBlur={e => (e.target.style.borderColor = T.border2)} />
              <div style={{ fontSize: 11, color: T.text3, marginTop: 6, lineHeight: 1.5 }}>The AI writes a unique opener for each contact using your <strong>Knowledge Base</strong> + this goal, then handles their replies automatically. Uses AI credits.</div>
            </>
          )}

          <button onClick={saveMessage} disabled={savingMsg} style={{ marginTop: 12, padding: "9px 20px", borderRadius: 9, border: "none", background: "#6366f1", color: "#fff", cursor: savingMsg ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font }}>
            {savingMsg ? "Saving…" : "Save Message"}
          </button>
        </div>

        {/* Add leads section */}
        <div style={{ background: T.bg1, border: `1px solid ${T.border2}`, borderRadius: 14, padding: "18px 20px", marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, marginBottom: 14 }}>Add Leads</div>

          {/* Link to a pipeline — every synced lead lands in its first stage automatically */}
          <div style={{ marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${T.border1}` }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>
              Add leads to pipeline {savingPipe && <span style={{ color: T.text3, fontWeight: 400, textTransform: "none" }}>· saving…</span>}
            </label>
            <select value={pipelineId} onChange={e => savePipeline(e.target.value)}
              style={{ width: "100%", padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 13, fontFamily: base.font, outline: "none", cursor: "pointer" }}>
              <option value="">None — don't add to a pipeline</option>
              {pipelines.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <div style={{ fontSize: 11, color: T.text3, marginTop: 6, lineHeight: 1.6 }}>
              When linked, each new lead is added to this pipeline's first stage (New Lead) automatically as it arrives — no need to wait for the message.
            </div>
          </div>

          {campaign.sourceType === "manual" && (
            <>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Phone Numbers (one per line — phone or phone,name)</label>
              <textarea value={phones} onChange={e => setPhones(e.target.value)} placeholder={"923001234567\n923001234568, Ahmed Khan\n923001234569, Sara Ali"} rows={5}
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 12, fontFamily: "monospace", outline: "none", resize: "vertical" }}
                onFocus={e => (e.target.style.borderColor = "#6366f1")} onBlur={e => (e.target.style.borderColor = T.border2)}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button onClick={addManual} style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: "#6366f1", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font }}>Add Leads</button>
                <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ padding: "9px 16px", borderRadius: 9, border: `1px solid ${T.border2}`, background: "none", color: T.text2, cursor: "pointer", fontSize: 13, fontFamily: base.font }}>
                  {uploading ? "Uploading…" : "📎 Upload CSV / Excel"}
                </button>
                <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: "none" }} onChange={e => { if (e.target.files?.[0]) { uploadCSV(e.target.files[0]); e.target.value = ""; } }} />
              </div>
              <div style={{ fontSize: 11, color: T.text3, marginTop: 8 }}>CSV or Excel (.xlsx) with a <strong>phone</strong> column header. <strong>name</strong> is optional.</div>
            </>
          )}

          {campaign.sourceType === "google_sheets" && (
            <>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Google Sheets URL (must be set to public)</label>
              <div style={{ display: "flex", gap: 10 }}>
                <input value={sheetsUrl} onChange={e => setSheetsUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…"
                  style={{ flex: 1, padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 12, fontFamily: base.font, outline: "none" }}
                  onFocus={e => (e.target.style.borderColor = "#6366f1")}
                  onBlur={e => { e.target.style.borderColor = T.border2; loadTabs(e.target.value); }}
                />
                <button onClick={syncSheets} disabled={syncing} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "#6366f1", color: "#fff", cursor: syncing ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font }}>
                  {syncing ? "Syncing…" : "Sync Now"}
                </button>
              </div>

              {/* Sheet tab (page) selector — a workbook can have many tabs */}
              {(loadingTabs || tabs.length > 0) && (
                <div style={{ marginTop: 10 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>
                    Which sheet tab? {loadingTabs && <span style={{ color: T.text3, fontWeight: 400, textTransform: "none" }}>· loading…</span>}
                  </label>
                  {tabs.length > 0 && (
                    <select value={selectedGid} onChange={e => setSelectedGid(e.target.value)}
                      style={{ width: "100%", padding: "10px 13px", background: T.bg0, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontSize: 13, fontFamily: base.font, outline: "none", cursor: "pointer" }}>
                      {tabs.map(t => <option key={t.gid} value={t.gid}>{t.name}</option>)}
                    </select>
                  )}
                </div>
              )}
              <div style={{ fontSize: 11, color: T.text3, marginTop: 8, lineHeight: 1.6 }}>
                Row 1 must be headers — include a <strong>phone</strong> column (<strong>name</strong> optional). In Google Sheets: <strong>Share → General access → “Anyone with the link → Viewer”</strong>, then paste the link above. The sheet auto-syncs every few minutes — or click <strong>Sync Now</strong> for an instant refresh. Contacts already added are skipped.
              </div>
            </>
          )}

          {campaign.sourceType !== "manual" && campaign.sourceType !== "google_sheets" && (
            <div style={{ fontSize: 12, color: T.text3 }}>
              This campaign uses an old source type. Click <strong>Edit</strong> and choose “Upload / Paste” or “Google Sheet” to add contacts.
            </div>
          )}
        </div>

        {/* Leads table */}
        <div style={{ background: T.bg1, border: `1px solid ${T.border2}`, borderRadius: 14, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border1}`, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, flex: 1 }}>Leads ({total})</div>
            {["","pending","sent","failed"].map(s => (
              <button key={s} onClick={() => { setFilter(s); loadLeads(1, s); }}
                style={{ padding: "4px 12px", borderRadius: 7, border: `1px solid ${filter === s ? "#6366f1" : T.border2}`, background: filter === s ? "rgba(99,102,241,0.1)" : "none", color: filter === s ? "#818cf8" : T.text3, cursor: "pointer", fontSize: 11, fontFamily: base.font }}>
                {s || "All"}
              </button>
            ))}
            {total > 0 && (
              <button onClick={clearLeads} title="Remove all leads"
                style={{ padding: "4px 12px", borderRadius: 7, border: "1px solid rgba(244,63,94,0.3)", background: "none", color: "#f43f5e", cursor: "pointer", fontSize: 11, fontFamily: base.font }}>
                Clear
              </button>
            )}
          </div>
          {leads.length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: T.text3, fontSize: 13 }}>No leads yet — add some above</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border1}` }}>
                  {["Phone","Name","Status","Sent At"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 700, color: T.text3, textAlign: "left", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l._id} style={{ borderBottom: `1px solid ${T.border1}` }}>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: T.text1 }}>+{l.phone}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: T.text2 }}>{l.name || "—"}</td>
                    <td style={{ padding: "10px 16px" }}><Pill status={l.status} cfg={leadStatusCfg} /></td>
                    <td style={{ padding: "10px 16px", fontSize: 12, color: T.text3 }}>{l.sentAt ? new Date(l.sentAt).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {total > 50 && (
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border1}`, display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); loadLeads(p, filter); }} disabled={page === 1} style={{ padding: "5px 14px", borderRadius: 7, border: `1px solid ${T.border2}`, background: "none", color: T.text2, cursor: "pointer", fontFamily: base.font, fontSize: 12 }}>←</button>
              <span style={{ fontSize: 12, color: T.text3 }}>Page {page} of {Math.ceil(total / 50)}</span>
              <button onClick={() => { const p = page + 1; setPage(p); loadLeads(p, filter); }} disabled={page >= Math.ceil(total / 50)} style={{ padding: "5px 14px", borderRadius: 7, border: `1px solid ${T.border2}`, background: "none", color: T.text2, cursor: "pointer", fontFamily: base.font, fontSize: 12 }}>→</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Campaign card ─────────────────────────────────────────────────
function CampaignCard({ campaign, onOpen, onDelete, onEdit }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const cfg = statusCfg[campaign.status] || statusCfg.draft;

  return (
    <div onClick={onOpen} style={{ background: T.bg1, border: `1.5px solid ${T.border2}`, borderRadius: 16, padding: "18px 20px", cursor: "pointer", transition: "border-color 0.15s", position: "relative" }}
      onMouseOver={e => e.currentTarget.style.borderColor = "#6366f155"}
      onMouseOut={e => e.currentTarget.style.borderColor = T.border2}>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `${cfg.color}18`, border: `1px solid ${cfg.color}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{campaign.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Pill status={campaign.status} cfg={statusCfg} />
            <span style={{ fontSize: 11, color: T.text3 }}>{campaign.sourceType === "google_sheets" ? "📊 Google Sheet" : "📋 Upload / Paste"}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
          <button onClick={onEdit} style={{ background: "none", border: `1px solid ${T.border2}`, borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: T.text3, fontSize: 11, fontFamily: base.font }}>Edit</button>
          {confirmDel ? (
            <>
              <button onClick={() => { onDelete(); setConfirmDel(false); }} style={{ background: "#f43f5e", border: "none", borderRadius: 7, padding: "4px 9px", cursor: "pointer", color: "#fff", fontSize: 11, fontFamily: base.font }}>Yes</button>
              <button onClick={() => setConfirmDel(false)} style={{ background: "none", border: `1px solid ${T.border2}`, borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: T.text3, fontSize: 11, fontFamily: base.font }}>No</button>
            </>
          ) : (
            <button onClick={() => setConfirmDel(true)} style={{ background: "none", border: `1px solid ${T.border2}`, borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: "#f43f5e", fontSize: 11, fontFamily: base.font }}>Del</button>
          )}
        </div>
      </div>

      <ProgressBar sent={campaign.sentCount || 0} total={campaign.totalLeads || 0} failed={campaign.failedCount || 0} />

      <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
        {[["Leads", campaign.totalLeads || 0], ["Sent", campaign.sentCount || 0], ["Failed", campaign.failedCount || 0]].map(([lbl, val]) => (
          <div key={lbl}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text1, lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: 10, color: T.text3, marginTop: 2 }}>{lbl}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function OutboundPage({ token, socket, showToast }) {
  const [campaigns,   setCampaigns]   = useState([]);
  const [pipelines,   setPipelines]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [editModal,   setEditModal]   = useState(null);

  const load = async () => {
    try {
      const [cd, pd] = await Promise.all([
        apiFetch("/api/outbound/campaigns", {}, token),
        apiFetch("/api/pipelines", {}, token),
      ]);
      if (cd?.campaigns) setCampaigns(cd.campaigns);
      if (pd?.pipelines) setPipelines(pd.pipelines);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [token]);

  // Live campaign status via socket
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (!data.campaignId) return;
      setCampaigns(prev => prev.map(c => c._id === data.campaignId
        ? { ...c, status: data.status || c.status, sentCount: data.sentCount ?? c.sentCount }
        : c));
      if (selected?._id === data.campaignId) {
        setSelected(prev => prev ? { ...prev, status: data.status || prev.status, sentCount: data.sentCount ?? prev.sentCount } : prev);
      }
    };
    socket.on("outbound_progress", handler);
    return () => socket.off("outbound_progress", handler);
  }, [socket, selected]);

  const createCampaign = async (data) => {
    const d = await apiFetch("/api/outbound/campaigns", { method: "POST", body: JSON.stringify(data) }, token);
    if (d.error) throw new Error(d.error);
    setCampaigns(prev => [d.campaign, ...prev]);
    setCreateModal(false);
    showToast("Campaign created");
  };

  const saveCampaign = async (data) => {
    const d = await apiFetch(`/api/outbound/campaigns/${editModal._id}`, { method: "PUT", body: JSON.stringify(data) }, token);
    if (d.error) throw new Error(d.error);
    setCampaigns(prev => prev.map(c => c._id === editModal._id ? d.campaign : c));
    setEditModal(null);
    showToast("Campaign updated");
  };

  const deleteCampaign = async (id) => {
    await apiFetch(`/api/outbound/campaigns/${id}`, { method: "DELETE" }, token);
    setCampaigns(prev => prev.filter(c => c._id !== id));
    if (selected?._id === id) setSelected(null);
    showToast("Campaign deleted");
  };

  const updateCampaigns = useCallback((updater) => {
    setCampaigns(updater);
    setSelected(prev => {
      if (!prev) return prev;
      const updated = typeof updater === "function" ? null : null;
      return prev; // Will be updated via the campaigns state sync
    });
  }, []);

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 24, height: 24, border: "2px solid rgba(255,255,255,0.1)", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );

  // Detail view
  if (selected) {
    const live = campaigns.find(c => c._id === selected._id) || selected;
    return (
      <CampaignDetail
        campaign={live}
        token={token}
        socket={socket}
        showToast={showToast}
        onUpdate={setCampaigns}
        onBack={() => setSelected(null)}
      />
    );
  }

  // Stats
  const totalLeads  = campaigns.reduce((s, c) => s + (c.totalLeads  || 0), 0);
  const totalSent   = campaigns.reduce((s, c) => s + (c.sentCount   || 0), 0);
  const totalFailed = campaigns.reduce((s, c) => s + (c.failedCount || 0), 0);
  const activeCount = campaigns.filter(c => c.status === "active").length;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px", fontFamily: base.font }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.text1, letterSpacing: "-0.02em" }}>Outbound Leads</div>
          <div style={{ fontSize: 12, color: T.text3, marginTop: 3 }}>Auto-message leads from forms, spreadsheets, or manual lists</div>
        </div>
        <button onClick={() => setCreateModal(true)} style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: base.font, display: "flex", alignItems: "center", gap: 7 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Campaign
        </button>
      </div>

      {/* Stats bar */}
      {campaigns.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
          {[
            ["Campaigns", campaigns.length, "#6366f1"],
            ["Total Leads", totalLeads, "#38bdf8"],
            ["Total Sent", totalSent, "#10b981"],
            ["Active Now", activeCount, "#f59e0b"],
          ].map(([lbl, val, color]) => (
            <div key={lbl} style={{ background: T.bg1, border: `1px solid ${T.border2}`, borderRadius: 14, padding: "16px 18px" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: "-0.04em" }}>{val}</div>
              <div style={{ fontSize: 11, color: T.text3, marginTop: 3 }}>{lbl}</div>
            </div>
          ))}
        </div>
      )}

      {/* Campaign grid */}
      {campaigns.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: T.text3 }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>📤</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text1, marginBottom: 8 }}>No campaigns yet</div>
          <div style={{ fontSize: 13, marginBottom: 22 }}>Create a campaign to start messaging leads automatically</div>
          <button onClick={() => setCreateModal(true)} style={{ padding: "11px 24px", borderRadius: 11, border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: base.font }}>
            Create First Campaign
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 16 }}>
          {campaigns.map(c => (
            <CampaignCard
              key={c._id}
              campaign={c}
              onOpen={() => setSelected(c)}
              onEdit={(e) => { e.stopPropagation?.(); setEditModal(c); }}
              onDelete={() => deleteCampaign(c._id)}
            />
          ))}
        </div>
      )}

      {createModal && (
        <CampaignModal pipelines={pipelines} onSave={createCampaign} onClose={() => setCreateModal(false)} />
      )}
      {editModal && (
        <CampaignModal initial={editModal} pipelines={pipelines} onSave={saveCampaign} onClose={() => setEditModal(null)} />
      )}
    </div>
  );
}
